import * as Sentry from "@sentry/react";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import type { Editor as TiptapEditor } from "@tiptap/react";
import { useSearchableRegistry, useSearchableVersion, type SearchResult } from "../../../lib/searchable";
import { getPositionOfRange } from "../Utils";
import Auth from "../../../Auth";
import { useProjectStore } from "../../../stores/projectStore";
import { useWebSocket } from "../../SocketHost/WebSocketProvider";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import { useToast } from "../../ToastWrapper";

export const SCOPE_PROJECT = "project";
export const SCOPE_DOCUMENT = "document";
export const SCOPE_STORY_BIBLE = "storyBible";

export type Scope = typeof SCOPE_PROJECT | typeof SCOPE_DOCUMENT | typeof SCOPE_STORY_BIBLE;

export interface ScopeOption {
  value: Scope;
  label: string;
  icon: {
    label: string;
    src: string;
    status: string;
  };
}

export const SCOPE_OPTIONS: ScopeOption[] = [
  {
    value: SCOPE_PROJECT,
    label: "Project",
    icon: { label: "Project", src: "/assets/folders.svg", status: "default" },
  },
  {
    value: SCOPE_DOCUMENT,
    label: "Document",
    icon: { label: "Document", src: "/assets/document.svg", status: "default" },
  },
  {
    value: SCOPE_STORY_BIBLE,
    label: "Story Bible",
    icon: { label: "Story Bible", src: "/assets/story-bible/story-bible-square.svg", status: "default" },
  },
];

/**
 * Editor wrapper containing a TipTap instance with search/replace extensions
 */
export interface EditorWrapper {
  tiptap: TiptapEditor & {
    commands: TiptapEditor["commands"] & {
      setSearchTerm: (term: string) => void;
      setReplaceTerm: (term: string) => void;
      setSelectedSearchResult: (index: number) => void;
    };
    storage: {
      searchAndReplace: {
        results: Array<{ from: number; to: number }>;
        lastSearchTerm?: string;
      };
    };
    view: TiptapEditor["view"];
    chain: TiptapEditor["chain"];
  };
}

export interface BackendDocumentResult {
  documentId: string;
  title: string;
  matchCount: number;
}

export interface BackendDocumentResults {
  totalMatches: number;
  totalDocuments: number;
  results: BackendDocumentResult[];
}

export interface ReplaceProgress {
  completed: number;
  total: number;
}

export interface UseFindAndReplaceStateOptions {
  /** The TipTap editor instance */
  editor: EditorWrapper | null | undefined;
  /** The current document ID (to exclude from backend search) */
  documentId?: string;
  /** The iframe content document element */
  editorHostNode?: HTMLElement | null;
  /** Ref to the scroll container */
  scrollHostRef?: RefObject<HTMLElement | null>;
}

export interface UseFindAndReplaceStateReturn {
  // State
  searchTerm: string;
  replaceTerm: string;
  selectedIndex: number;
  isSearching: boolean;
  scope: Scope;
  globalResults: SearchResult[];
  backendDocumentResults: BackendDocumentResults | null;
  isSearchingBackend: boolean;
  isReplacingBackend: boolean;
  replaceProgress: ReplaceProgress | null;

  // Computed
  hasGlobalSearch: boolean;
  hasStoryBibleSearchables: boolean;
  isGlobalMode: boolean;
  searchResultSummary: string;
  hasResults: boolean;
  canReplace: boolean;

  // Actions
  handleSearchTermChange: (value: string) => void;
  handleReplaceTermChange: (value: string) => void;
  handleScopeChange: (newScope: Scope) => void;
  handleNext: () => void;
  handlePrevious: () => void;
  replaceCurrentResult: () => void;
  handleReplaceAll: () => Promise<void>;
  clearSearchState: () => void;
}

/**
 * Custom hook that encapsulates all find and replace state and logic.
 * Reduces complexity in the FindAndReplace component.
 */
export function useFindAndReplaceState({
  editor,
  documentId,
  editorHostNode,
  scrollHostRef,
}: UseFindAndReplaceStateOptions): UseFindAndReplaceStateReturn {
  // Track mounted state to avoid state updates after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // Core search state
  const [searchTerm, setSearchTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  // Scope state
  const [scope, setScope] = useState<Scope>(SCOPE_DOCUMENT);
  const [globalResults, setGlobalResults] = useState<SearchResult[]>([]);

  // Backend document search results
  const [backendDocumentResults, setBackendDocumentResults] = useState<BackendDocumentResults | null>(null);
  const [isSearchingBackend, setIsSearchingBackend] = useState(false);
  const [isReplacingBackend, setIsReplacingBackend] = useState(false);
  const [replaceProgress, setReplaceProgress] = useState<ReplaceProgress | null>(null);
  const [resultsVersion, setResultsVersion] = useState(0);

  // Get projectId from store
  const project = useProjectStore((state) => state.project);
  const projectId = project?.id;

  // Toast for user-friendly error messages
  const { setToast } = useToast();

  // Socket for real-time progress updates
  const { registerHandler, unregisterHandler } = useWebSocket();

  // Context
  const { searchAll, replaceResult, replaceAllGlobal, scrollToResult, getSearchables, updateSearchState } =
    useSearchableRegistry();
  const searchableVersion = useSearchableVersion();

  // Check if project has multiple documents (enables Project scope)
  const hasMultipleDocuments = useMemo(() => {
    const contents = project?.data?.contents?.contents;
    if (!Array.isArray(contents)) return false;
    return contents.filter((item: any) => item.type === 'document').length > 1;
  }, [project?.data?.contents?.contents]);

  // Check if Story Bible searchables are registered (enables Story Bible scope)
  // searchableVersion ensures this recomputes when searchables register/unregister
  const hasStoryBibleSearchables = useMemo(() => {
    const searchables = getSearchables();
    return searchables.some((s) => s.id !== 'document');
  }, [getSearchables, searchableVersion]);

  // Check if global search is available (has searchables registered OR project has multiple docs)
  // searchableVersion ensures this recomputes when searchables register/unregister
  const hasGlobalSearch = useMemo(() => {
    const searchables = getSearchables();
    return searchables.length > 1 || hasMultipleDocuments;
  }, [getSearchables, hasMultipleDocuments, searchableVersion]);

  const effectiveScope = scope === SCOPE_STORY_BIBLE && !hasStoryBibleSearchables ? SCOPE_DOCUMENT : scope;

  // Determine if we're in a mode that searches beyond just the document
  const isGlobalMode = effectiveScope !== SCOPE_DOCUMENT && hasGlobalSearch;

  // Get current results based on mode
  const getCurrentResults = useCallback((): Array<{ from: number; to: number }> | SearchResult[] => {
    if (isGlobalMode) {
      return globalResults;
    }
    return editor?.tiptap?.storage?.searchAndReplace?.results || [];
  }, [isGlobalMode, globalResults, editor]);

  // Scroll to document result
  const scrollToDocumentResult = useCallback(() => {
    if (!editor || !editor.tiptap || !editorHostNode) return;
    const results = editor.tiptap.storage.searchAndReplace.results;
    const resultsLength = results.length;
    if (resultsLength === 0) return;

    const result = results[selectedIndex];
    if (!result) return;
    const { from, to } = result;
    const position = getPositionOfRange(editor.tiptap, editorHostNode, from, to);

    const scrollHost = scrollHostRef?.current;
    if (scrollHost) {
      const scrollHostRect = scrollHost.getBoundingClientRect();
      const editorHostRect = editorHostNode.getBoundingClientRect();
      // getPositionOfRange is editor-host-relative; convert it to scroll-host content coordinates.
      const resultPosition =
        scrollHost.scrollTop + (editorHostRect.top - scrollHostRect.top) + position.top;
      scrollHost.scrollTop = Math.max(0, resultPosition - scrollHost.clientHeight / 2);
    }
  }, [editor, editorHostNode, scrollHostRef, selectedIndex]);

  // Searchables select and scroll without taking focus from the user's active control.
  const scrollToGlobalResult = useCallback(
    () => {
      const result = globalResults[selectedIndex];
      if (result) {
        scrollToResult(result);
      }
    },
    [globalResults, selectedIndex, scrollToResult]
  );

  // Debounced search for document mode
  const debouncedDocumentSearch = useCallback(
    debounce((value: string, ed: EditorWrapper) => {
      // PERF: Discard stale results if user has typed a newer term
      if (value !== latestSearchTermRef.current) return;

      ed.tiptap.commands.setSearchTerm(value);
      ed.tiptap.commands.setSelectedSearchResult(0);
      setIsSearching(false);

    }, 300),
    []
  );

  // Search backend documents
  const searchBackendDocuments = useCallback(
    async (term: string) => {
      if (!projectId || !term) {
        setBackendDocumentResults(null);
        return;
      }

      setIsSearchingBackend(true);
      try {
        const response = await Auth.fetchWithIdToken(`/api/projects/${projectId}/documents/find-replace`, {
          searchTerm: term,
          replaceTerm: "",
          preview: true,
        }) as BackendDocumentResults;

        // PERF: Discard stale backend responses — if the user has typed a newer term
        // while the fetch was in flight, don't show results for the old term.
        if (!mountedRef.current || term !== latestSearchTermRef.current) return;

        setBackendDocumentResults(response);
      } catch (error) {
        if (!mountedRef.current) return;
        Sentry.captureException(error, { tags: { projectId: projectId || "unknown", feature: "find_replace_search" } });
        setToast("Failed to search documents. Please try again.");
        setBackendDocumentResults(null);
      } finally {
        if (mountedRef.current) setIsSearchingBackend(false);
      }
    },
    [projectId]
  );

  // Track the latest search term to discard stale debounce results
  const latestSearchTermRef = useRef("");

  // Debounced search for global/scoped mode
  const debouncedGlobalSearch = useCallback(
    debounce((value: string, ed: EditorWrapper | null | undefined, searchScope: Scope) => {
      // PERF: Discard stale results — if the user has already typed a newer term,
      // don't update results for this outdated one.
      if (value !== latestSearchTermRef.current) return;

      let results = searchAll(value);

      // Filter results based on scope
      if (searchScope === SCOPE_STORY_BIBLE) {
        // Only Story Bible fields (exclude document)
        results = results.filter((r) => r.sourceId !== "document");
      }
      // SCOPE_PROJECT includes all results, no filtering needed

      // React 18 createRoot batches these automatically, even in setTimeout/debounce callbacks.
      setGlobalResults(results);
      setIsSearching(false);

      // PERF: Defer document highlighting to next animation frame.
      // TipTap's search extension takes ~96ms to re-search the document synchronously.
      // By deferring to rAF, results display instantly and highlights follow one frame later.
      if (ed?.tiptap) {
        if (searchScope === SCOPE_PROJECT) {
          requestAnimationFrame(() => {
            if (value !== latestSearchTermRef.current || !ed?.tiptap) return;
            ed.tiptap.commands.setSearchTerm(value);
            ed.tiptap.commands.setSelectedSearchResult(0);
          });
        } else {
          // Clear document highlighting for Story Bible only mode
          ed.tiptap.commands.setSearchTerm("");
          ed.tiptap.commands.setSelectedSearchResult(-1);
        }
      }

      // Also search backend documents for Project scope.
      // Call directly (not debounced) since debouncedGlobalSearch is already debounced.
      // Double-debouncing created a 300ms gap where stale backend results mixed with fresh frontend results.
      if (searchScope === SCOPE_PROJECT) {
        searchBackendDocuments(value);
      }
    }, 300),
    [searchAll, searchBackendDocuments]
  );

  // Handle search term changes
  const handleSearchTermChange = useCallback(
    (value: string) => {
      const wasEmpty = !latestSearchTermRef.current;
      latestSearchTermRef.current = value;
      setSearchTerm(value);
      setSelectedIndex(0);

      if (!value) {
        // Only clear highlights when the search field is emptied
        setIsSearching(false);
        if (editor?.tiptap) {
          editor.tiptap.commands.setSearchTerm("");
          editor.tiptap.commands.setSelectedSearchResult(-1);
        }
        setGlobalResults([]);
        setBackendDocumentResults(null);
        return;
      }

      setIsSearching(true);

      // Clear stale backend results immediately so the summary shows "Searching..."
      // instead of mixing old backend counts with new frontend results.
      if (effectiveScope === SCOPE_PROJECT) {
        setBackendDocumentResults(null);
      }

      // Off-viewport RestorableTiptapField placeholders only mount their real editor
      // (and register with the searchable registry) when isSearchActive flips true.
      // Broadcast on the empty→non-empty transition so fields mount before the 300ms
      // debounced searchAll runs — otherwise the search would miss every off-screen field.
      if (wasEmpty && (effectiveScope === SCOPE_PROJECT || effectiveScope === SCOPE_STORY_BIBLE)) {
        updateSearchState(value, null);
        lastBroadcastRef.current = { term: value, resultKey: "" };
      }

      // PERF: Don't clear highlights on every keystroke. Let the previous results
      // stay visible until the debounced search replaces them with new ones.
      // This prevents the flicker of highlights disappearing then reappearing.
      if (effectiveScope === SCOPE_DOCUMENT) {
        if (editor) {
          debouncedDocumentSearch(value, editor);
        }
      } else {
        debouncedGlobalSearch(value, editor, effectiveScope);
      }
    },
    [editor, effectiveScope, debouncedDocumentSearch, debouncedGlobalSearch, updateSearchState]
  );

  // Handle replace term changes
  const handleReplaceTermChange = useCallback(
    (value: string) => {
      setReplaceTerm(value);
      if (!isGlobalMode && editor?.tiptap) {
        editor.tiptap.commands.setReplaceTerm(value);
      }
    },
    [isGlobalMode, editor]
  );

  // Handle scope changes
  const handleScopeChange = useCallback(
    (newScope: Scope) => {
      const nextScope = newScope === SCOPE_STORY_BIBLE && !hasStoryBibleSearchables ? SCOPE_DOCUMENT : newScope;
      setScope(nextScope);
      setSelectedIndex(0);
      setGlobalResults([]);

      // Clear backend results when switching away from Project scope
      if (nextScope !== SCOPE_PROJECT) {
        setBackendDocumentResults(null);
      }

      // Clear existing search and re-search with new scope
      if (searchTerm) {
        setIsSearching(true);

        // Switching from DOCUMENT → global scope while a term is active means the
        // off-viewport fields haven't been mounted yet. Broadcast eagerly so they
        // register before the debounced searchAll runs (same race as in handleSearchTermChange).
        if (nextScope === SCOPE_PROJECT || nextScope === SCOPE_STORY_BIBLE) {
          updateSearchState(searchTerm, null);
          lastBroadcastRef.current = { term: searchTerm, resultKey: "" };
        }

        if (nextScope === SCOPE_DOCUMENT) {
          if (editor) {
            debouncedDocumentSearch(searchTerm, editor);
          }
        } else {
          debouncedGlobalSearch(searchTerm, editor, nextScope);
        }
      }
    },
    [searchTerm, editor, debouncedDocumentSearch, debouncedGlobalSearch, updateSearchState, hasStoryBibleSearchables]
  );

  useEffect(() => {
    if (scope === SCOPE_STORY_BIBLE && !hasStoryBibleSearchables) {
      handleScopeChange(SCOPE_DOCUMENT);
    }
  }, [scope, hasStoryBibleSearchables, handleScopeChange]);

  // Re-apply retained search term when document/editor changes (e.g. user switches documents).
  // Waits for editor.tiptap.isEditable — this is only true after onSynced, meaning the
  // document content has loaded via Y.js and the search will produce correct results.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const term = latestSearchTermRef.current;
    if (!term || !editor?.tiptap?.isEditable) return;

    setSelectedIndex(0);

    if (effectiveScope === SCOPE_DOCUMENT) {
      // Apply search directly (no debounce needed — content is already loaded).
      // Must use forceViewUpdate() because setSearchTerm doesn't dispatch a transaction,
      // so the ProseMirror plugin won't re-run and results would stay empty.
      editor.tiptap.commands.setSearchTerm(term);
      editor.tiptap.commands.setSelectedSearchResult(0);
      forceViewUpdate();
      setIsSearching(false);
    } else {
      setIsSearching(true);
      if (effectiveScope === SCOPE_PROJECT) {
        setBackendDocumentResults(null);
      }
      debouncedGlobalSearch(term, editor, effectiveScope);
    }
  }, [editor, editor?.tiptap?.isEditable, documentId]);

  // Keep React in sync with TipTap's search results.
  // The ProseMirror search plugin updates storage.results on every transaction where
  // the doc or search term changes. Since this is not React state, we listen for
  // transactions and bump resultsVersion when results change, so searchResultSummary
  // recomputes.
  useEffect(() => {
    const tiptap = editor?.tiptap;
    if (!tiptap) return;

    let lastResultsLength = tiptap.storage?.searchAndReplace?.results?.length ?? 0;

    const onTransaction = () => {
      const currentLength = tiptap.storage?.searchAndReplace?.results?.length ?? 0;
      if (currentLength !== lastResultsLength) {
        lastResultsLength = currentLength;
        setResultsVersion((v) => v + 1);
      }
    };

    tiptap.on("transaction", onTransaction);
    return () => {
      tiptap.off("transaction", onTransaction);
    };
  }, [editor?.tiptap]);

  // Navigate to next result
  const handleNext = useCallback(() => {
    const results = getCurrentResults();
    const resultsLength = results.length;
    if (resultsLength === 0) return;

    let nextIndex = selectedIndex + 1;
    if (nextIndex >= resultsLength) nextIndex = 0;

    if (selectedIndex === nextIndex) {
      if (isGlobalMode) {
        scrollToGlobalResult();
      } else {
        scrollToDocumentResult();
      }
    } else {
      setSelectedIndex(nextIndex);
    }
  }, [getCurrentResults, selectedIndex, isGlobalMode, scrollToGlobalResult, scrollToDocumentResult]);

  // Navigate to previous result
  const handlePrevious = useCallback(() => {
    const results = getCurrentResults();
    const resultsLength = results.length;
    if (resultsLength === 0) return;

    let nextIndex = selectedIndex - 1;
    if (nextIndex < 0) nextIndex = resultsLength - 1;

    setSelectedIndex(nextIndex);
  }, [getCurrentResults, selectedIndex]);

  // Replace current result
  const replaceCurrentResult = useCallback(() => {
    const results = getCurrentResults();
    const resultsLength = results.length;
    if (resultsLength === 0) return;

    const result = results[selectedIndex];
    if (!result) return;

    // Check if the replacement still contains the search term (case-insensitive).
    // If so, we need to advance to the next result to avoid getting stuck
    // on the same position (e.g., replacing "rabbits" with "crabbits").
    const replacementContainsSearch = replaceTerm.toLowerCase().includes(searchTerm.toLowerCase());

    if (isGlobalMode) {
      replaceResult(result as SearchResult, replaceTerm);
      // Re-search after replace to update results
      setIsSearching(true);
      setTimeout(() => {
        const newResults = searchAll(searchTerm);
        setGlobalResults(newResults);
        setIsSearching(false);

        if (newResults.length === 0) {
          setSelectedIndex(0);
        } else if (replacementContainsSearch) {
          // Advance to next result since the current position still matches
          const nextIndex = (selectedIndex + 1) % newResults.length;
          setSelectedIndex(nextIndex);
        } else if (selectedIndex >= newResults.length) {
          // Result was removed, adjust index
          setSelectedIndex(Math.max(0, newResults.length - 1));
        }
        // Otherwise keep same index (now pointing to what was the next result)
      }, 50);
    } else {
      editor?.tiptap.chain().insertContentAt({ from: result.from, to: result.to }, replaceTerm).run();

      if (replacementContainsSearch) {
        // Advance to next result since the current position still matches
        const nextIndex = (selectedIndex + 1) % resultsLength;
        setSelectedIndex(nextIndex);
      } else if (selectedIndex === resultsLength - 1) {
        // Was at last result, wrap to beginning
        setSelectedIndex(0);
      }
      // Otherwise keep same index (now pointing to what was the next result)
      scrollToDocumentResult();
    }
  }, [
    getCurrentResults,
    selectedIndex,
    replaceTerm,
    searchTerm,
    isGlobalMode,
    replaceResult,
    searchAll,
    editor,
    scrollToDocumentResult,
  ]);

  // Helper to force TipTap view update after Hocuspocus sync
  // This is needed because commands like setSearchTerm don't dispatch transactions,
  // and Firefox may not automatically refresh the view after Y.Doc sync
  const forceViewUpdate = useCallback(() => {
    if (editor?.tiptap?.view) {
      // Clear lastSearchTerm to force decoration rebuild on next apply
      editor.tiptap.storage.searchAndReplace.lastSearchTerm = "";
      // Dispatch an empty transaction to trigger plugin apply methods
      editor.tiptap.view.dispatch(editor.tiptap.view.state.tr);
    }
  }, [editor]);

  // Replace all results
  const handleReplaceAll = useCallback(async () => {
    if (effectiveScope === SCOPE_DOCUMENT) {
      // Document scope - use backend to replace just the current document
      // This will auto-sync to the editor via Hocuspocus
      if (projectId && documentId) {
        setIsSearchingBackend(true);
        setIsReplacingBackend(true);
        try {
          await Auth.fetchWithIdToken(`/api/projects/${projectId}/documents/find-replace`, {
            searchTerm,
            replaceTerm,
            preview: false,
            onlyDocumentId: documentId, // Only process this specific document
          });

          // Wait for Hocuspocus to sync the changes, then re-trigger search to update counts
          setTimeout(() => {
            if (!mountedRef.current) return;
            // Re-apply the same search term to update the result counts
            editor?.tiptap.commands.setSearchTerm(searchTerm);
            editor?.tiptap.commands.setSelectedSearchResult(0);
            // Force view update for Firefox and other browsers that may not auto-refresh
            forceViewUpdate();
            setIsSearching(false);
          }, 1000); // Give time for sync
        } catch (error) {
          Sentry.captureException(error, { tags: { projectId: projectId || "unknown", feature: "find_replace_document" } });
          if (!mountedRef.current) return;
          setToast("Failed to replace text in document. Please try again.");
          setIsSearching(false);
        } finally {
          if (!mountedRef.current) return;
          setIsSearchingBackend(false);
          setIsReplacingBackend(false);
        }
      }
    } else {
      setIsSearching(true);
      setIsReplacingBackend(true);

      if (effectiveScope === SCOPE_PROJECT) {
        // Project scope - replace in Story Bible (frontend) + all documents (backend)
        // Story Bible fields are handled by the frontend (not documents)
        const searchables = getSearchables();
        for (const searchable of searchables) {
          if (searchable.id !== "document") {
            try {
              searchable.replaceAll(searchTerm, replaceTerm);
            } catch (e) {
              Sentry.captureException(e, { tags: { projectId: projectId || "unknown", feature: "find_replace_story_bible", field: searchable.name } });
              setToast(`Failed to replace text in ${searchable.name}. Please try again.`);
            }
          }
        }

        // All documents (including current) are handled by the backend for consistency
        // This ensures all documents get pre-replacement snapshots
        const docsToReplace = backendDocumentResults?.results?.filter(
          (doc) => doc.matchCount > 0
        ) || [];

        if (projectId && docsToReplace.length > 0) {
          setIsSearchingBackend(true);
          setIsReplacingBackend(true);
          setReplaceProgress({ completed: 0, total: docsToReplace.length });

          try {
            // Perform replacement on ALL documents (backend handles snapshots)
            // Hocuspocus will sync changes back to the editor for current document
            await Auth.fetchWithIdToken(`/api/projects/${projectId}/documents/find-replace`, {
              searchTerm,
              replaceTerm,
              preview: false,
              // No excludeDocumentId - backend handles all documents including current
            });

            // Clear TipTap search highlights after backend replacement syncs
            setTimeout(() => {
              if (!mountedRef.current) return;
              if (editor?.tiptap) {
                editor.tiptap.commands.setSearchTerm(searchTerm);
                editor.tiptap.commands.setSelectedSearchResult(0);
                // Force view update for Firefox and other browsers that may not auto-refresh
                forceViewUpdate();
              }
            }, 1000);
          } catch (error) {
            Sentry.captureException(error, { tags: { projectId: projectId || "unknown", feature: "find_replace_all_documents" } });
            if (!mountedRef.current) return;
            setToast("Failed to replace text across documents. Please try again.");
            setIsSearchingBackend(false);
            setIsReplacingBackend(false);
            setReplaceProgress(null);
          }
        }
      } else if (effectiveScope === SCOPE_STORY_BIBLE) {
        // Story Bible only - replace in all searchables except document
        const searchables = getSearchables();
        for (const searchable of searchables) {
          if (searchable.id !== "document") {
            try {
              searchable.replaceAll(searchTerm, replaceTerm);
            } catch (e) {
              Sentry.captureException(e, { tags: { projectId: projectId || "unknown", feature: "find_replace_story_bible", field: searchable.name } });
              setToast(`Failed to replace text in ${searchable.name}. Please try again.`);
            }
          }
        }

        // CRITICAL: Force save after Story Bible replacements to ensure changes persist
        // Wait for React state updates to propagate first
        await new Promise((resolve) => setTimeout(resolve, 200));

        const forceSave = useStoryBibleStore.getState().forceSaveRef.current;
        if (forceSave && typeof forceSave === "function") {
          try {
            forceSave();
          } catch (e) {
            // Silently handle errors - forceSave will retry automatically via usePersist
          }
        }
      }

      // Wait for updates to propagate before searching again
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (!mountedRef.current) return;

      // Final search to update UI (with scope filtering)
      let finalResults = searchAll(searchTerm);
      if (effectiveScope === SCOPE_STORY_BIBLE) {
        finalResults = finalResults.filter((r) => r.sourceId !== "document");
      }
      setGlobalResults(finalResults);
      setSelectedIndex(0);
      setIsSearching(false);
      setIsReplacingBackend(false);
    }
  }, [effectiveScope, editor, searchTerm, replaceTerm, getSearchables, searchAll, projectId, forceViewUpdate, documentId, backendDocumentResults]);

  // Listen for real-time progress updates via socket
  useEffect(() => {
    interface ProgressPayload {
      completed: number;
      total: number;
      documentId?: string;
      replaced?: boolean;
    }

    const handleProgress = (msg: { payload: ProgressPayload }) => {
      const progress = msg.payload;
      setReplaceProgress({ completed: progress.completed, total: progress.total });

      // Update backend results in real-time
      // Update count to 0 for replaced documents OR if it's the current document (handled by TipTap)
      if (progress.replaced || progress.documentId === documentId) {
        setBackendDocumentResults((prev) => {
          if (!prev) return prev;

          // Update the specific document's match count to 0 after replacement
          const updatedResults = prev.results.map((doc) =>
            doc.documentId === progress.documentId
              ? { ...doc, matchCount: 0 }
              : doc
          );

          // Recalculate totals
          const totalMatches = updatedResults.reduce((sum, doc) => sum + doc.matchCount, 0);
          const totalDocuments = updatedResults.filter((doc) => doc.matchCount > 0).length;

          return {
            totalMatches,
            totalDocuments,
            results: updatedResults,
          };
        });
      }
    };

    const handleComplete = () => {
      // Final update when all replacements are done
      // Keep dialog open - user will close it manually
      setReplaceProgress(null);
      setIsSearchingBackend(false);
      setIsReplacingBackend(false);
    };

    registerHandler("FIND_REPLACE_PROGRESS", handleProgress);
    registerHandler("FIND_REPLACE_COMPLETE", handleComplete);

    return () => {
      unregisterHandler("FIND_REPLACE_PROGRESS");
      unregisterHandler("FIND_REPLACE_COMPLETE");
    };
  }, [registerHandler, unregisterHandler, documentId]);

  // Clear search state and cache on unmount or when dialog closes
  const clearSearchState = useCallback(() => {
    if (editor && editor.tiptap) {
      editor.tiptap.commands.setSearchTerm("");
      editor.tiptap.commands.setReplaceTerm("");
      editor.tiptap.commands.setSelectedSearchResult(-1);
    }
    updateSearchState("", null);
    setReplaceProgress(null);
    setBackendDocumentResults(null); // Clear cached backend results
  }, [editor, updateSearchState]);

  // Clear backend cache when dialog opens (on mount)
  useEffect(() => {
    setBackendDocumentResults(null);
  }, []);

  // Broadcast search state to searchables for highlighting in Story Bible fields
  // Track last broadcast to avoid redundant updates that trigger 100+ field re-renders
  const lastBroadcastRef = useRef<{ term: string; resultKey: string }>({ term: "", resultKey: "" });

  useEffect(() => {
    if (isGlobalMode && !isSearching) {
      const selectedResult = globalResults[selectedIndex] || null;
      // PERF: Skip broadcast if nothing actually changed
      const resultKey = selectedResult ? `${selectedResult.sourceId}:${selectedResult.from}` : "";
      if (lastBroadcastRef.current.term === searchTerm && lastBroadcastRef.current.resultKey === resultKey) {
        return;
      }
      lastBroadcastRef.current = { term: searchTerm, resultKey };
      updateSearchState(searchTerm, selectedResult);
    } else if (!isGlobalMode) {
      // PERF: Skip clearing if already cleared
      if (lastBroadcastRef.current.term === "" && lastBroadcastRef.current.resultKey === "") {
        return;
      }
      lastBroadcastRef.current = { term: "", resultKey: "" };
      // Clear highlighting in Story Bible when in document-only mode
      updateSearchState("", null);
    }
  }, [searchTerm, globalResults, selectedIndex, isSearching, isGlobalMode, updateSearchState]);

  // Handle scrolling and selection updates
  useEffect(() => {
    if (!isSearching) {
      if (isGlobalMode) {
        scrollToGlobalResult();
        // Also update TipTap's selected result for document matches
        const currentResult = globalResults[selectedIndex];
        if (currentResult && currentResult.sourceId === "document" && editor?.tiptap) {
          // Find the index within the TipTap results
          const tiptapResults = editor.tiptap.storage?.searchAndReplace?.results || [];
          const tiptapIndex = tiptapResults.findIndex((r) => r.from === currentResult.from && r.to === currentResult.to);
          if (tiptapIndex >= 0) {
            editor.tiptap.commands.setSelectedSearchResult(tiptapIndex);
          }
        }
      } else {
        scrollToDocumentResult();
        if (editor && editor.tiptap) {
          editor.tiptap.commands.setSelectedSearchResult(selectedIndex);
        }
      }
    }
  }, [selectedIndex, searchTerm, isSearching, isGlobalMode, scrollToGlobalResult, globalResults, editor, scrollToDocumentResult]);

  // Compute result summary
  const searchResultSummary = useMemo(() => {
    if (isSearching) return searchTerm ? "Searching..." : "";

    const results = getCurrentResults();
    const resultsLength = results.length;

    if (searchTerm) {
      // For Project scope, combine Story Bible counts (from frontend) with backend document counts
      if (effectiveScope === SCOPE_PROJECT) {
        // Story Bible results come from frontend (instant), document results from backend (async)
        const storyBibleCount = (results as SearchResult[]).filter((r) => r.sourceId !== "document").length;

        // Show partial results immediately while backend loads
        const backendLoading = isSearchingBackend || !backendDocumentResults;
        const backendMatches = backendDocumentResults?.totalMatches ?? 0;
        const backendDocs = backendDocumentResults?.totalDocuments ?? 0;

        if (storyBibleCount === 0 && backendLoading) {
          return "Searching documents...";
        }

        if (storyBibleCount === 0 && backendMatches === 0) {
          return "0 found";
        }

        // Build parts of the summary
        const parts: string[] = [];
        if (storyBibleCount > 0) {
          parts.push(`${storyBibleCount} in Story Bible`);
        }
        if (backendLoading) {
          parts.push("searching documents...");
        } else if (backendMatches > 0) {
          parts.push(`${backendMatches} in ${backendDocs} ${backendDocs === 1 ? "document" : "documents"}`);
        }

        const total = storyBibleCount + backendMatches;
        const prefix = backendLoading ? `${storyBibleCount} found so far` : `${total} found`;
        return `${prefix} (${parts.join(", ")})`;
      }

      // For other scopes, use existing logic
      if (resultsLength === 0) {
        return "0 found";
      }

      const currentResult = (results as SearchResult[])[selectedIndex];
      const sourceLabel = isGlobalMode && currentResult?.sourceName ? ` (${currentResult.sourceName})` : "";

      if (resultsLength === 1) {
        return `1 result${sourceLabel}`;
      }
      return `${selectedIndex + 1} of ${resultsLength}${sourceLabel}`;
    }
    return "";
  }, [isSearching, isSearchingBackend, searchTerm, selectedIndex, isGlobalMode, getCurrentResults, effectiveScope, backendDocumentResults, resultsVersion]);

  // Computed values
  const results = getCurrentResults();
  const hasResults = results.length > 0;

  // For Project scope, also check if backend has results
  const hasBackendResults = effectiveScope === SCOPE_PROJECT && backendDocumentResults !== null && backendDocumentResults.totalMatches > 0;
  const canReplace = (hasResults || hasBackendResults) && searchTerm.length > 0 && replaceTerm.length > 0;

  return {
    // State
    searchTerm,
    replaceTerm,
    selectedIndex,
    isSearching,
    scope: effectiveScope,
    globalResults,
    backendDocumentResults,
    isSearchingBackend,
    isReplacingBackend,
    replaceProgress,

    // Computed
    hasGlobalSearch,
    hasStoryBibleSearchables,
    isGlobalMode,
    searchResultSummary,
    hasResults,
    canReplace,

    // Actions
    handleSearchTermChange,
    handleReplaceTermChange,
    handleScopeChange,
    handleNext,
    handlePrevious,
    replaceCurrentResult,
    handleReplaceAll,
    clearSearchState,
  };
}

export default useFindAndReplaceState;
